import React from 'react';
import type { BarberGrowthSnapshot } from '../../services/barberDataService';

/**
 * Growth Score badge (Phase 5) — the auto-computed business/development score + tier + stage.
 * Complements (does not replace) the BPI/PPI badges. Renders NULL when a barber has no growth
 * snapshot yet (legacy docs before the first recompute) → safe to mount anywhere.
 * BEHAVIOR-PIN: SLIK-GROWTH-SYSTEM-BLUEPRINT.md · math = shared/growth.ts.
 */
const TIER_STYLE: Record<string, { bg: string; text: string; border: string; icon: string; label: string }> = {
  gold:   { bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-300',  icon: '🥇', label: 'Gold' },
  silver: { bg: 'bg-slate-100',  text: 'text-slate-700 dark:text-slate-400',  border: 'border-slate-300',  icon: '🥈', label: 'Silver' },
  bronze: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: '🥉', label: 'Bronze' },
  none:   { bg: 'bg-gray-100',   text: 'text-gray-600 dark:text-gray-300',   border: 'border-gray-300',   icon: '🌱', label: 'Building' },
};

export const GrowthScoreCard: React.FC<{ growth?: BarberGrowthSnapshot | null; size?: 'sm' | 'md' }> = ({ growth, size = 'md' }) => {
  if (!growth) return null;
  const t = TIER_STYLE[growth.tier] || TIER_STYLE.none;
  const pad = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <div className={`inline-flex flex-col items-start gap-0.5`}>
      <div className={`inline-flex items-center gap-2 rounded-full border ${t.bg} ${t.text} ${t.border} ${pad} font-semibold touch-manipulation`}>
        <span>{t.icon}</span>
        <span>Growth {growth.score}</span>
        <span className="opacity-90">·</span>
        <span>{t.label}{growth.provisional ? '*' : ''}</span>
      </div>
      <div className="text-[11px] text-white/90">
        Stage {growth.stage} · {growth.stageName}
        {growth.bonusEligible && growth.bonusPct > 0 && (
          <span> · +{growth.bonusPct}% bonus</span>
        )}
      </div>
    </div>
  );
};

export default GrowthScoreCard;
